import { useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  X, 
  Upload, 
  Code, 
  Image as ImageIcon, 
  VideoCamera, 
  MusicNote,
  Trash
} from "@phosphor-icons/react";

export default function CreatePostModal({ open, onClose, onPostCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [htmlCode, setHtmlCode] = useState("");
  const [cssCode, setCssCode] = useState("");
  const [jsCode, setJsCode] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const fileInputRef = useRef(null);

  const imagePreviewUrls = useMemo(
    () => files.map((file) => (file.type.startsWith("image/") ? URL.createObjectURL(file) : null)),
    [files]
  );

  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [imagePreviewUrls]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setTags("");
    setHtmlCode("");
    setCssCode("");
    setJsCode("");
    setFiles([]);
    setActiveTab("details");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(file => {
      const isValid = file.type.startsWith("image/") || 
                      file.type.startsWith("video/") || 
                      file.type.startsWith("audio/");
      if (!isValid) {
        toast.error(`${file.name} is not a supported file type`);
      }
      return isValid;
    });
    setFiles([...files, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("tags", tags);
      
      if (htmlCode) formData.append("html_code", htmlCode);
      if (cssCode) formData.append("css_code", cssCode);
      if (jsCode) formData.append("js_code", jsCode);
      
      files.forEach(file => {
        formData.append("files", file);
      });

      const response = await apiClient.post("/posts", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Post created!");
      onPostCreated(response.data);
      handleClose();
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 429) {
        toast.error(detail || "Daily post limit reached. You can post again tomorrow.", {
          duration: 6000,
        });
      } else {
        toast.error("Failed to create post");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith("image/")) return ImageIcon;
    if (file.type.startsWith("video/")) return VideoCamera;
    if (file.type.startsWith("audio/")) return MusicNote;
    return Upload;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden bg-surface border-border/40 p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="font-heading text-xl">Create New Post</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Share your project with the DIIT community
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="w-full justify-start px-6 bg-transparent border-b border-border/40 rounded-none h-auto py-0">
              <TabsTrigger 
                value="details" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3"
              >
                Details
              </TabsTrigger>
              <TabsTrigger 
                value="media" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3"
              >
                Media
              </TabsTrigger>
              <TabsTrigger 
                value="code" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3"
              >
                Code
              </TabsTrigger>
            </TabsList>

            <div className="p-6 overflow-y-auto max-h-[50vh]">
              <TabsContent value="details" className="mt-0 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="My awesome project"
                    className="bg-background border-border focus:border-primary"
                    data-testid="post-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Tell us about your project..."
                    rows={4}
                    className="bg-background border-border focus:border-primary resize-none"
                    data-testid="post-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="react, javascript, web"
                    className="bg-background border-border focus:border-primary"
                    data-testid="post-tags-input"
                  />
                </div>
              </TabsContent>

              <TabsContent value="media" className="mt-0 space-y-4">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors"
                >
                  <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="font-medium mb-1">Click to upload</p>
                  <p className="text-sm text-muted-foreground">
                    Images, videos, or audio files
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*,audio/*"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                </div>

                {files.length > 0 && (
                  <div className="space-y-2">
                    {files.map((file, index) => {
                      const Icon = getFileIcon(file);
                      return (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 bg-background rounded-lg"
                        >
                          {imagePreviewUrls[index] ? (
                            <img
                              src={imagePreviewUrls[index]}
                              alt={`Preview ${file.name}`}
                              className="w-12 h-12 rounded-md object-cover shrink-0 border border-border"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-md bg-surface border border-border flex items-center justify-center shrink-0">
                              <Icon className="w-5 h-5 text-primary" />
                            </div>
                          )}
                          <span className="flex-1 truncate text-sm">{file.name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {(file.size / 1024 / 1024).toFixed(1)} MB
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="p-1 text-muted-foreground hover:text-danger transition-colors"
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="code" className="mt-0 space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Add HTML, CSS, and JavaScript code for a live preview in your post.
                </p>
                <p className="text-xs text-muted-foreground -mt-3">
                  Tip: You can paste iframe embed snippets (for example music or video widgets) directly in HTML.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="html" className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-danger/20 text-danger text-xs rounded">HTML</span>
                  </Label>
                  <Textarea
                    id="html"
                    value={htmlCode}
                    onChange={(e) => setHtmlCode(e.target.value)}
                    placeholder="<div>Hello World</div>"
                    rows={4}
                    className="bg-background border-border focus:border-primary resize-none font-mono text-sm"
                    data-testid="post-html-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="css" className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">CSS</span>
                  </Label>
                  <Textarea
                    id="css"
                    value={cssCode}
                    onChange={(e) => setCssCode(e.target.value)}
                    placeholder="div { color: red; }"
                    rows={4}
                    className="bg-background border-border focus:border-primary resize-none font-mono text-sm"
                    data-testid="post-css-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="js" className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-warning/20 text-warning text-xs rounded">JS</span>
                  </Label>
                  <Textarea
                    id="js"
                    value={jsCode}
                    onChange={(e) => setJsCode(e.target.value)}
                    placeholder="console.log('Hello');"
                    rows={4}
                    className="bg-background border-border focus:border-primary resize-none font-mono text-sm"
                    data-testid="post-js-input"
                  />
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <div className="p-6 pt-4 border-t border-border/40 flex justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
              data-testid="submit-post-button"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Posting...
                </span>
              ) : (
                "Create Post"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
